import _extends from "@babel/runtime/helpers/extends";
import React, { memo, useCallback, useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, Popover, Icon, Modal, Textarea, Text, Carousel } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../../../lib/Web5Context";
import styles from "./carouselCellStyles";
const useStyles = createUseStyles(styles, 'Carousel');
const CarouselCell = _ref => {
  var _data$otherFields$car;
  let {
    data,
    otherFields,
    value,
    node,
    isReadOnly,
    inputParams,
    imageSize = 'medium',
    isSquared = false,
    withEmptyPlaceholder
  } = _ref;
  const [isEllipsisActive, setEllipsisActive] = useState(false);
  const descriptionEl = useRef();
  const [description, setDescription] = useState(data === null || data === void 0 ? void 0 : data[otherFields === null || otherFields === void 0 ? void 0 : otherFields.secondaryTextKey]);
  const [isOpen, setIsOpen] = useState();
  const {
    t
  } = useWeb5Context();
  const classes = useStyles();
  const carouselCellClassName = classnames('w5-carousel-cell', {
    'w5-carousel-cell--small': imageSize === 'small',
    'w5-carousel-cell--medium': imageSize === 'medium',
    'w5-carousel-cell--big': imageSize === 'big'
  });
  const boxImageClassName = classnames('w5-carousel-cell__img-box', {
    'w5-carousel-cell__img-box--small': imageSize === 'small',
    'w5-carousel-cell__img-box--medium': imageSize === 'medium',
    'w5-carousel-cell__img-box--big': imageSize === 'big',
    'w5-carousel-cell__img-box--squared': isSquared,
    'w5-carousel-cell__img-box__with-popover': (otherFields === null || otherFields === void 0 ? void 0 : otherFields.carouselFieldId) && data[otherFields.carouselFieldId]
  });
  const carouselPopoverClassName = classnames('w5-carousel-cell__img-box__popover', classes.popover, {
    [classes.singleImagePopover]: ((_data$otherFields$car = data[otherFields === null || otherFields === void 0 ? void 0 : otherFields.carouselFieldId]) === null || _data$otherFields$car === void 0 ? void 0 : _data$otherFields$car.length) === 1
  });
  useEffect(() => {
    if (isOpen) setDescription(data === null || data === void 0 ? void 0 : data[otherFields === null || otherFields === void 0 ? void 0 : otherFields.secondaryTextKey]);
  }, [data, isOpen, otherFields === null || otherFields === void 0 ? void 0 : otherFields.secondaryTextKey]);
  useLayoutEffect(() => {
    const el = descriptionEl.current;
    if (!el || !data) return;
    setEllipsisActive(el.offsetWidth < el.scrollWidth);
  }, [data, otherFields === null || otherFields === void 0 ? void 0 : otherFields.secondaryTextKey, setEllipsisActive]);
  const [mainImageSrc, setMainImageSrc] = useState(() => {
    return (data === null || data === void 0 ? void 0 : data[otherFields === null || otherFields === void 0 ? void 0 : otherFields.rowImageSrc]) || (data === null || data === void 0 ? void 0 : data[otherFields === null || otherFields === void 0 ? void 0 : otherFields.rowImagePlaceholder]);
  });
  const onLoadMainImageError = useCallback(() => {
    setMainImageSrc(data === null || data === void 0 ? void 0 : data[otherFields === null || otherFields === void 0 ? void 0 : otherFields.rowImagePlaceholder]);
  }, [data, otherFields]);
  const onChangeDescription = useCallback(value => {
    if (!inputParams) return;
    const {
      onChange
    } = inputParams;
    const id = data.id || data.Id || data.ID || '';
    onChange({
      rowIndex: node.rowIndex,
      id,
      field: otherFields === null || otherFields === void 0 ? void 0 : otherFields.secondaryTextKey,
      value
    });
  }, [data.ID, data.Id, data.id, inputParams, node.rowIndex, otherFields === null || otherFields === void 0 ? void 0 : otherFields.secondaryTextKey]);
  const imagesCarousel = useMemo(() => {
    if (!data[otherFields === null || otherFields === void 0 ? void 0 : otherFields.carouselFieldId]) return null;
    const images = data[otherFields === null || otherFields === void 0 ? void 0 : otherFields.carouselFieldId].map((image, index) => {
      const imageUrl = image[otherFields.carouselImageSrc] || image[otherFields.carouselImagePlaceholder];
      return {
        original: imageUrl,
        thumbnail: imageUrl
      };
    });
    return /*#__PURE__*/React.createElement(Carousel, {
      images: images
    });
  }, [data, otherFields]);
  var _ref2 = /*#__PURE__*/React.createElement("div", {
    className: boxImageClassName
  }, /*#__PURE__*/React.createElement("img", {
    src: mainImageSrc,
    alt: value,
    onError: onLoadMainImageError
  }));
  const imageBox = useMemo(() => {
    const imageBox = _ref2;
    if (imagesCarousel) {
      return /*#__PURE__*/React.createElement(Popover, {
        className: carouselPopoverClassName,
        placement: "right",
        content: imagesCarousel
      }, imageBox);
    }
    return imageBox;
  }, [boxImageClassName, mainImageSrc, value, onLoadMainImageError, imagesCarousel, carouselPopoverClassName]);
  const showDescriptionForm = useCallback(() => setIsOpen(true), []);
  const onConfirm = useCallback(() => {
    onChangeDescription(description);
    setIsOpen(false);
  }, [description, onChangeDescription]);
  const onClose = useCallback(() => setIsOpen(false), []);
  const modalEditProps = useMemo(() => ({
    isOpen,
    size: 'small',
    onConfirm,
    onCancel: onClose,
    onRequestClose: onClose,
    cancelText: t('action_cancel'),
    confirmText: t('action_save'),
    overlayClassName: classes.overlay
  }), [classes.overlay, isOpen, onClose, onConfirm, t]);
  const modalViewProps = useMemo(() => ({
    isOpen,
    size: 'medium',
    onCancel: onClose,
    onRequestClose: onClose,
    cancelText: t('action_close'),
    overlayClassName: classes.overlay
  }), [classes.overlay, isOpen, onClose, t]);
  var _ref3 = /*#__PURE__*/React.createElement(Icon, {
    name: "visibilityOn",
    size: "medium",
    onClick: showDescriptionForm
  });
  var _ref4 = /*#__PURE__*/React.createElement(Icon, {
    name: "edit",
    size: "medium",
    onClick: showDescriptionForm
  });
  const descriptionModal = useMemo(() => {
    if (isReadOnly) {
      if (!isEllipsisActive) return null;
      return /*#__PURE__*/React.createElement(React.Fragment, null, _ref3, /*#__PURE__*/React.createElement(Modal, _extends({
        useHeader: false
      }, modalViewProps), /*#__PURE__*/React.createElement(Text, null, data === null || data === void 0 ? void 0 : data[otherFields === null || otherFields === void 0 ? void 0 : otherFields.secondaryTextKey])));
    } else {
      return /*#__PURE__*/React.createElement(React.Fragment, null, _ref4, /*#__PURE__*/React.createElement(Modal, _extends({
        useHeader: false
      }, modalEditProps), /*#__PURE__*/React.createElement(Textarea, {
        className: classes.textarea,
        placeholder: t('placeholder_text_field'),
        minRows: 8,
        onChange: setDescription,
        value: description,
        isFullWidth: true
      })));
    }
  }, [classes.textarea, data, description, isEllipsisActive, isReadOnly, modalEditProps, modalViewProps, otherFields === null || otherFields === void 0 ? void 0 : otherFields.secondaryTextKey, showDescriptionForm, t]);
  if (withEmptyPlaceholder && !value) value = t('label_placeholder_unamed_record');
  const carouselCellTextClassName = classnames('w5-carousel-cell', {
    'fm-entity-placeholder': withEmptyPlaceholder && !value
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "w5-cell",
    "data-rowid": node.id
  }, /*#__PURE__*/React.createElement("div", {
    className: carouselCellClassName
  }, /*#__PURE__*/React.createElement("div", {
    className: "w5-carousel-cell__img-container"
  }, imageBox), /*#__PURE__*/React.createElement("div", {
    className: "w5-carousel-cell__text-wrapper"
  }, /*#__PURE__*/React.createElement("div", {
    className: carouselCellTextClassName
  }, value), /*#__PURE__*/React.createElement("div", {
    className: "w5-carousel-cell__description"
  }, /*#__PURE__*/React.createElement("span", {
    className: "w5-carousel-cell__description-text",
    ref: descriptionEl
  }, data === null || data === void 0 ? void 0 : data[otherFields === null || otherFields === void 0 ? void 0 : otherFields.secondaryTextKey]), descriptionModal))));
};
process.env.NODE_ENV !== "production" ? CarouselCell.propTypes = {
  imageSize: PropTypes.oneOf(['small', 'medium', 'big']),
  isSquared: PropTypes.bool
} : void 0;
export default /*#__PURE__*/memo(CarouselCell);